import { HttpParams } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { FlashMessagesService } from 'angular2-flash-messages'
import { combineLatest, forkJoin } from 'rxjs'
import { map } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'
import { appConstants } from '../../../app.constants'
import { Category } from '../../../common/interfaces/category.interface'
import { Granulometry } from '../../../common/interfaces/granulometry.interface'
import { Mission } from '../../../common/interfaces/mission.interface'
import { Sample } from '../../../common/interfaces/sample.interface'
import { Subcategory } from '../../../common/interfaces/subcategory.interface'
import { Tare } from '../../../common/interfaces/tare.interface'
import { User } from '../../../common/interfaces/user.interface'
import { BreadcrumbService } from '../../../common/services/breadcrumb.service'
import { ResourceService } from '../../../common/services/resource.service'
import { AuthService } from '../../../common/services/auth.service'
import { Role } from '../../../common/enums/role.enum'
import { ConnectivityService } from '../../../common/services/offline/connectivity.service'
import { OfflineResourceService } from '../../../common/services/offline/offline-resource.service'

interface CategoryTreeNode {
  id: number
  name: string
  position: number
  expanded: boolean
  subcategories: { id: number; name: string; position: number }[]
}

@Component({
  selector: 'app-mission-detail',
  templateUrl: './mission-detail.component.html',
  styleUrls: ['./mission-detail.component.scss'],
  providers: [ResourceService]
})
export class MissionDetailComponent implements OnInit {
  mission: Mission
  users: User[] = []
  tareToDelete: Tare
  granulometryToDelete: Granulometry
  storagePath: string = environment.storagePath
  loading: boolean
  isOnline = true
  currentUser: User
  Role = Role
  togglingActive = false

  // Category/subcategory treeview used to filter the samples list below it.
  categoryTree: CategoryTreeNode[] = []
  filteredSamples: Sample[] = []
  selectedCategoryId: number = null
  selectedSubcategoryId: number = null
  // subcategoryId -> categoryId, to resolve a weighing's category from its subcategory
  private subcategoryToCategoryId: { [subcategoryId: number]: number } = {}

  constructor(
    private resourceService: ResourceService,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private flashMessagesService: FlashMessagesService,
    private connectivityService: ConnectivityService,
    private offlineResourceService: OfflineResourceService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.connectivityService.isOnline$.subscribe(online => {
      this.isOnline = online
    })

    this.authService.currentUser.subscribe((userRes: User) => {
      this.currentUser = userRes
    })

    // Subscribe to params and query params. QueryParams subscription reload Mission on tare/granulometry change.
    combineLatest(this.activatedRoute.params, this.activatedRoute.queryParams)
      .pipe(map((results) => ({ params: results[0], queryParams: results[1] })))
      .subscribe(
        (res: { params: { id: string }; queryParams: { reload: string } }) => {
          this.getMission(res.params.id)
        }
      )
  }

  getMission(id: string): void {
    this.loading = true

    if (!this.connectivityService.isOnline) {
      // Offline: load from IndexedDB
      Promise.all([
        this.offlineResourceService.getMissions().then(
          missions => missions.find(m => m.id === parseInt(id, 10))
        ),
        this.offlineResourceService.getSamples(id)
      ]).then(
        ([mission, samples]) => {
          this.loading = false
          if (!mission) {
            this.flashMessagesService.show(
              'Mission non disponible hors connexion.',
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
            return
          }
          this.processMissionData(mission, samples)
        },
        () => {
          this.loading = false
          this.flashMessagesService.show(
            'Une erreur a eu lieu : Impossible de récupérer le(s) élémént(s) désiré(s).',
            {
              cssClass: 'notification is-danger',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            }
          )
        }
      )
      return
    }

    let sampleParams: HttpParams = new HttpParams()
    sampleParams = sampleParams.set('missionIds', id)
    sampleParams = sampleParams.set('withoutPagination', 'true')
    forkJoin([
      this.resourceService.show('missions', id),
      this.resourceService.list('samples', sampleParams)
    ]).subscribe(
      ([missionRes, sampleRes]: [Mission, Sample[]]) => {
        this.loading = false
        this.processMissionData(missionRes, sampleRes)

        // Cache this mission with full data (including subcategories from show endpoint)
        this.offlineResourceService.cacheMission(missionRes).catch(() => {})
      },
      (error) => {
        this.loading = false
        this.flashMessagesService.show(
          'Une erreur a eu lieu : Impossible de récupérer le(s) élémént(s) désiré(s).',
          {
            cssClass: 'notification is-danger',
            timeout: appConstants.FLASH_MESSAGE_TIMEOUT
          }
        )
      }
    )
  }

  private processMissionData(mission: Mission, samples: Sample[]): void {
    this.mission = mission
    this.mission.samples = samples
    this.mission.weighingCount = 0

    this.mission.samples.forEach((sample: Sample) => {
      // Get all User from mission sample
      if (sample.user && !this.users.find((u) => u.id === sample.user.id)) {
        this.users.push(sample.user)
      }

      // Calculate total weighings
      this.mission.weighingCount =
        this.mission.weighingCount + (sample.weighings ? sample.weighings.length : 0)
    })

    this.buildCategoryTree()
    // No filter selected yet: show every sample.
    this.clearFilter()

    this.breadcrumbService.breadcrumbLinks.next([
      {
        path: '/missions',
        label: 'Missions'
      },
      {
        label: this.mission.name
      }
    ])
  }

  // Groups the mission's subcategories by their parent category, sorted by rank.
  private buildCategoryTree(): void {
    this.categoryTree = []
    this.subcategoryToCategoryId = {}
    const nodesById: { [categoryId: number]: CategoryTreeNode } = {}

    ;(this.mission.subcategories || []).forEach((subcategory: Subcategory) => {
      const category: Category = subcategory.category
      if (!category) {
        return
      }

      this.subcategoryToCategoryId[subcategory.id] = category.id

      let node: CategoryTreeNode = nodesById[category.id]
      if (!node) {
        node = {
          id: category.id,
          name: category.name,
          position: category.position || 0,
          expanded: true,
          subcategories: []
        }
        nodesById[category.id] = node
        this.categoryTree.push(node)
      }

      node.subcategories.push({
        id: subcategory.id,
        name: subcategory.name,
        position: subcategory.position || 0
      })
    })

    this.categoryTree.sort(
      (a, b) => a.position - b.position || a.name.localeCompare(b.name)
    )
    this.categoryTree.forEach((node) =>
      node.subcategories.sort(
        (a, b) => a.position - b.position || a.name.localeCompare(b.name)
      )
    )
  }

  // Active/désactive la mission (Admin/LocalAdmin — la route détail est déjà gardée).
  toggleActive(): void {
    if (!this.mission || this.togglingActive) {
      return
    }
    this.togglingActive = true
    this.resourceService
      .patch('missions', this.mission.id, 'toggle-active', new FormData())
      .subscribe(
        () => {
          this.togglingActive = false
          this.mission.isActive = !this.mission.isActive
          this.flashMessagesService.show(
            this.mission.isActive
              ? 'La mission a été activée'
              : 'La mission a été désactivée',
            {
              cssClass: 'notification is-success',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            }
          )
        },
        () => {
          this.togglingActive = false
          this.flashMessagesService.show(
            'Une erreur a eu lieu : impossible de modifier l\'état de la mission.',
            {
              cssClass: 'notification is-danger',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            }
          )
        }
      )
  }

  toggleCategoryExpand(node: CategoryTreeNode): void {
    node.expanded = !node.expanded
  }

  filterByCategory(categoryId: number): void {
    this.selectedCategoryId = categoryId
    this.selectedSubcategoryId = null
    this.applyFilter()
  }

  filterBySubcategory(subcategoryId: number, categoryId: number): void {
    this.selectedCategoryId = categoryId
    this.selectedSubcategoryId = subcategoryId
    this.applyFilter()
  }

  clearFilter(): void {
    this.selectedCategoryId = null
    this.selectedSubcategoryId = null
    this.applyFilter()
  }

  isCategoryActive(categoryId: number): boolean {
    return (
      this.selectedCategoryId === categoryId &&
      this.selectedSubcategoryId === null
    )
  }

  isSubcategoryActive(subcategoryId: number): boolean {
    return this.selectedSubcategoryId === subcategoryId
  }

  // Recomputes the displayed samples from the current category/subcategory filter.
  // A sample matches when at least one of its weighings targets the selected
  // subcategory (or any subcategory of the selected category).
  private applyFilter(): void {
    const samples: Sample[] = this.mission.samples || []

    if (this.selectedSubcategoryId !== null) {
      this.filteredSamples = samples.filter((sample) =>
        this.sampleMatchesSubcategory(sample, this.selectedSubcategoryId)
      )
    } else if (this.selectedCategoryId !== null) {
      this.filteredSamples = samples.filter((sample) =>
        this.sampleMatchesCategory(sample, this.selectedCategoryId)
      )
    } else {
      this.filteredSamples = samples
    }
  }

  private sampleMatchesSubcategory(
    sample: Sample,
    subcategoryId: number
  ): boolean {
    return (
      !!sample.weighings &&
      sample.weighings.some(
        (w) => w.subcategory && w.subcategory.id === subcategoryId
      )
    )
  }

  private sampleMatchesCategory(sample: Sample, categoryId: number): boolean {
    return (
      !!sample.weighings &&
      sample.weighings.some(
        (w) =>
          w.subcategory &&
          this.subcategoryToCategoryId[w.subcategory.id] === categoryId
      )
    )
  }
}
